// import external libraries

import { createApp } from 'vue';
import Buefy from 'buefy';

import '@/assets/images/fontawesome.js';

// noinspection ES6UnusedImports
import _ from 'lodash-es';

import * as Sentry from '@sentry/vue';
import { scrubBreadcrumb, scrubEvent, scrubSpan } from '@/app/util/sentry_scrubbing';
import { Vue3Mq } from 'vue3-mq';

import VueConfetti from 'vue-confetti';
// import internal Libraries
// noinspection ES6UnusedImports
import App from './App.vue';
import eventBus from '@/plugins/event_bus';
import breadcrumbs from '@/plugins/breadcrumbs/breadcrumbs';
import Avatar from '@/plugins/avatar/Avatar.vue';
import FormatCurrency from '@/app/shared_components/currency/FormatCurrency.vue';
import GlobalConfirmModal from '@/app/shared_components/modals/GlobalConfirmModal.vue';
import buttons from '@/app/shared_components/buttons/buttons.js';
import tags from '@/app/shared_components/tags/tags.js';
import i18n from '@/i18n/index';
import router from '@/router/index';
import { AuthPlugin } from '@/auth';
import store from '@/store/index';
import primevue from '@/plugins/primevue';
import toasts from '@/app/util/toasts';
import sequentialPromise from '@/app/util/synchronous_promise';
import VPopper from '@/app/shared_components/tooltips/VPopper.vue';
import TitleSmall from '@/app/shared_components/titles/TitleSmall.vue';
import TitleMedium from '@/app/shared_components/titles/TitleMedium.vue';
import TitleForm from '@/app/shared_components/titles/TitleForm.vue';
import Modal from '@/app/shared_components/modals/TheModal.vue';
import truncate from '@/plugins/truncate';

// import constants
import { ZIGGU_API_URL } from '@/app/util/urls';
import flipper from '@/plugins/flipper';
import { FT } from '@/app/data/flipper_constants';

// import firebase
import { initializeApp } from 'firebase/app';

import { getAnalytics } from 'firebase/analytics';

// import css stylesheets
import '@/assets/css/index.css';
import AppIcon from '@/app/shared_components/icons/AppIcon.vue';
import AppLink from '@/app/shared_components/AppLink.vue';
import ExternalLink from '@/app/shared_components/ExternalLink.vue';
import Pagination from '@/app/shared_components/pagination/Pagination.vue';
import INJECT_KEYS from '@/app/data/inject_keys';
import installPolyfills from '@/plugins/polyfills';

installPolyfills();

const app = createApp(App);

app.use({
  install() {
    app.config.globalProperties.emitter = eventBus;
    app.provide(INJECT_KEYS.EMITTER, eventBus);
  },
});

app.use(store);
app.use(router);
app.use(i18n);

app.use(Buefy, { defaultIconPack: 'fas' });

// Initialize Sentry
if (!import.meta.env.IS_LOCAL) {
  Sentry.init({
    app,
    dsn: 'https://be16e9a319304d88b103e2300a4f669f@sentry.io/1190623',
    release: `${import.meta.env.RELEASE_VERSION}`,
    environment: `${import.meta.env.VITE_ENV}`,
    ignoreErrors: [
      'TypeError: NetworkError when attempting to fetch resource.',
      'Non-Error exception captured',
      'Non-Error promise rejection captured',
      'ResizeObserver loop completed with undelivered notifications.',
      // Ignore MutationObserver error from vue3-popper
      "Failed to execute 'observe' on 'MutationObserver': parameter 1 is not of type 'Node'.",
      // https://github.com/nico3333fr/CSP-useful/blob/master/csp-wtf/explained.md#apple-quicklook
      "Refused to evaluate a string as JavaScript because 'unsafe-eval' is not " +
        'an allowed source of script in the following Content Security Policy directive: ' +
        '"default-src x-apple-ql-id: \'unsafe-inline\'"',
      // expired Google Cloud Storage URLs for PDFs
      'Unexpected server response (400) while retrieving PDF',
      // PDF-related errors
      'InvalidPDFException',
      /Invalid PDF structure/i,
      /UnknownErrorException: Load failed/i,
      // Safari/iOS IndexedDB instability during Firebase Auth init
      /Database deleted by request of the user/i,
      /UnknownError:.*Database deleted by request of the user/i,
      /UnknownError: Connection to Indexed Database server lost/i,
      // Network-related errors
      /^Network Error$/i,
      /Failed to fetch/i,
      /NetworkError when attempting to fetch resource/i,
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
        tracePropagationTargets: [`${ZIGGU_API_URL.replace('https://', '')}`],
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.25, // percentage
    normalizeDepth: 8,
    sendDefaultPii: false,
    attachProps: false,
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSendTransaction: scrubEvent,
    beforeSendSpan: scrubSpan,

    // prevent 401 error logs (expired sessions, see api/index.js) being sent to Sentry
    // oxlint-disable complexity
    beforeSend(event, hint) {
      const isUnauthorizedError =
        hint &&
        hint.originalException &&
        hint.originalException.response &&
        hint.originalException.response.status &&
        // 401: user unauthorized (legitimate in general)
        // 403: resource unauthorized (legitimate in general, e.g. stale data due to cache)
        _.includes([401, 403], hint.originalException.response.status);
      const isSentryDiscard =
        hint &&
        hint.originalException &&
        hint.originalException.sentry_discard &&
        hint.originalException.sentry_discard === true;
      const isAPIError =
        hint &&
        hint.originalException &&
        hint.originalException.response &&
        hint.originalException.response.status &&
        // 500: API raised an unexpected error, Sentry will log this on API-side, logging of APP side error is redundant
        hint.originalException.response.status === 500;
      const isChunkError =
        (event.message && event.message.startsWith('Loading chunk ')) ||
        (event.name && event.name === 'ChunkLoadError');
      // Filter out errors only when www appears before a subdomain (e.g., www.subdomain.domain.com)
      const hostnameParts = window.location.hostname.split('.');
      const isWWWSubdomain = hostnameParts.length > 2 && hostnameParts[0] === 'www';
      // ignore network errors in general, typically noise, mostly due to Safari https://github.com/axios/axios/issues/4420
      const isNetworkError =
        (event.name && event.name === 'AxiosError' && event.message && event.message === 'Network Error') ||
        (hint &&
          hint.originalException &&
          hint.originalException.message &&
          (hint.originalException.message === 'Network Error' ||
            hint.originalException.message.includes('Failed to fetch') ||
            hint.originalException.message.includes('NetworkError')));
      // ignore errors (presumably) triggered by Chrome/Firefox extensions
      const isCustomUnhandledRejectionEvent =
        event &&
        event.message &&
        event.message.includes('CustomEvent {type:unhandledrejection} captured as promise rejection');

      if (
        isUnauthorizedError ||
        isSentryDiscard ||
        isChunkError ||
        isNetworkError ||
        isAPIError ||
        isWWWSubdomain ||
        isCustomUnhandledRejectionEvent
      ) {
        return null;
      }

      return scrubEvent(event);
    },
  });
}

if (import.meta.env.IS_LOCAL) {
  app.config.performance = true;
}

// Initialize authentication
app.use(AuthPlugin);

// Initialize Firebase
const app_firebase = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
});
getAnalytics(app_firebase);

app.use({
  install() {
    window._ = _;
    app.config.globalProperties.window = window;
    app.config.globalProperties.$_ = _;
    app.config.globalProperties.$FT = FT;
  },
});
app.config.globalProperties.sequentialPromise = sequentialPromise;

app.use(VueConfetti);
app.mixin(toasts);

app.component('Avatar', Avatar);
app.component('FormatCurrency', FormatCurrency);
app.component('GlobalConfirmModal', GlobalConfirmModal);
app.component('Modal', Modal);
app.component('VPopper', VPopper);
app.component('TitleSmall', TitleSmall);
app.component('TitleMedium', TitleMedium);
app.component('TitleForm', TitleForm);
app.component('Pagination', Pagination);
app.component('AppIcon', AppIcon);
app.component('AppLink', AppLink);
app.component('ExternalLink', ExternalLink);

app.use(primevue);

app.use(breadcrumbs);
app.use(flipper, store);
app.use(buttons);
app.use(tags);
app.use(Vue3Mq);

const truncatePlugin = {
  install() {
    app.truncate = truncate;
    app.config.globalProperties.$truncate = truncate;
  },
};
app.use(truncatePlugin);

// load error handling for active sessions (silent reload the page)
// https://vite.dev/guide/build#load-error-handling
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

app.mount('#app');
